import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Equipment, EquipmentForm, EquipmentSearchForm } from 'entities/equipment'

type EquipmentsResponse = PagingResponse & {
    results: Equipment[]
}

export function useFetchEquipmentsApi(searchForm?: Form<EquipmentSearchForm>): IndexApiSet<EquipmentsResponse> & { execute: () => void } {
    const apiPath = 'equipments/'
    const api = useIndexApi<EquipmentsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvEquipmentsApi(searchForm: Form<EquipmentSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'equipments/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'equipment', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostEquipmentApi(): ApiSet<BaseResponse> & { execute: (form: Form<EquipmentForm>) => void } {
    const apiSet = usePostApi<BaseResponse, EquipmentForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<EquipmentForm>) => {
        const apiPath = `equipments/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutEquipmentApi(): ApiSet<BaseResponse> & { execute: (form: Form<EquipmentForm>) => void } {
    const apiSet = usePutApi<BaseResponse, EquipmentForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<EquipmentForm>) => {
        const apiPath = `equipments/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutEquipmentsApi(): ApiSet<BaseResponse> & { execute: (form: Form<EquipmentForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, EquipmentForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<EquipmentForm[]>) => {
        const apiPath = `equipments/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteEquipmentApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `equipments/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
